import React from 'react';
import HelpBadge from './components/HelpBadge';
import GlossaryTerm from './components/GlossaryTerm';
import { isRealGatewayAccessToken } from './gatewayAuth';
import { isRoleAccessDeniedMessage } from './subscriberNavAccess';
import { trackUxEvent } from './uxTelemetry';
import './InletWorkspace.css';

type InletWorkspaceProps = {
  gatewayBaseUrl: string;
  accessToken: string;
  language?: 'en' | 'es';
  onSignIn?: () => void;
  onAccessDenied?: () => void;
};

type Partner = {
  id: number;
  code: string;
  name: string;
  status: string;
  contact_email?: string | null;
  commission_bps?: number;
};

type Conversion = {
  event_id?: number;
  event_type: string;
  email?: string | null;
  partner_code?: string | null;
  partner_status?: string;
  source?: string | null;
  created_at?: string;
};

type PartnerSummary = {
  partner: Partner;
  landing_url: string;
  conversion_count: number;
  trial_paid_count: number;
  newsletter_count: number;
  assistant_capture_count: number;
  recent: Conversion[];
};

const InletWorkspace: React.FC<InletWorkspaceProps> = ({
  gatewayBaseUrl,
  accessToken,
  language = 'en',
  onSignIn,
  onAccessDenied,
}) => {
  const spanish = language === 'es';
  const hasSession = isRealGatewayAccessToken(accessToken);
  const [partners, setPartners] = React.useState<Partner[]>([]);
  const [conversions, setConversions] = React.useState<Conversion[]>([]);
  const [summary, setSummary] = React.useState<PartnerSummary | null>(null);
  const [selectedCode, setSelectedCode] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [name, setName] = React.useState('');
  const [commissionBps, setCommissionBps] = React.useState('0');

  const callApi = React.useCallback(
    async (path: string, init?: RequestInit) => {
      if (!isRealGatewayAccessToken(accessToken)) {
        throw new Error(
          spanish
            ? 'Inicia sesión con una cuenta de gateway para usar Inlet.'
            : 'Sign in with a gateway account to use Inlet.',
        );
      }
      const response = await fetch(`${gatewayBaseUrl}${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...((init?.headers as Record<string, string> | undefined) || {}),
        },
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { detail?: unknown };
        const detail = typeof payload.detail === 'string' ? payload.detail : `Inlet returned ${response.status}`;
        throw new Error(detail);
      }
      return response.json();
    },
    [accessToken, gatewayBaseUrl, spanish],
  );

  const load = React.useCallback(async (partnerCode?: string) => {
    setBusy(true);
    setError('');
    try {
      const [partnerPayload, conversionPayload] = await Promise.all([
        callApi('/api/inlet/partners'),
        callApi('/api/inlet/conversions?limit=50'),
      ]);
      const nextPartners = Array.isArray(partnerPayload.partners) ? partnerPayload.partners as Partner[] : [];
      const nextConversions = Array.isArray(conversionPayload.conversions)
        ? conversionPayload.conversions as Conversion[]
        : [];
      setPartners(nextPartners);
      setConversions(nextConversions);
      const chosen = (partnerCode || selectedCode || nextPartners[0]?.code || '').trim();
      if (chosen) {
        setSelectedCode(chosen);
        const nextSummary = await callApi(`/api/inlet/partners/${encodeURIComponent(chosen)}/summary`);
        setSummary(nextSummary as PartnerSummary);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (isRoleAccessDeniedMessage(message)) {
        onAccessDenied?.();
        return;
      }
      setError(message);
      trackUxEvent({
        event_type: 'product_error',
        product: 'inlet',
        last_path: window.location.pathname,
        message,
      });
    } finally {
      setBusy(false);
    }
  }, [callApi, onAccessDenied, selectedCode]);

  React.useEffect(() => {
    if (!hasSession) {
      return;
    }
    void load();
    // First paint only; later loads are explicit (select / register).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession]);

  const registerPartner = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await callApi('/api/inlet/partners', {
        method: 'POST',
        body: JSON.stringify({
          code: code.trim().toLowerCase(),
          name: name.trim(),
          commission_bps: Number(commissionBps) || 0,
        }),
      });
      setCode('');
      setName('');
      setCommissionBps('0');
      await load(code.trim().toLowerCase());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      trackUxEvent({
        event_type: 'product_error',
        product: 'inlet',
        last_path: window.location.pathname,
        message: err instanceof Error ? err.message : String(err),
      });
      setBusy(false);
    }
  };

  if (!hasSession) {
    return (
      <section className="inlet-console" aria-label="Inlet">
        <div className="inlet-signin inlet-welcome">
          <div className="inlet-welcome-emoji" aria-hidden="true">🌊</div>
          <h3>{spanish ? 'Inlet = puerta de entrada de amigos' : 'Inlet = the friend door'}</h3>
          <p>
            {spanish
              ? 'Aquí registra a socios que envían gente a su negocio. Inicie sesión para empezar — es como poner un cartel en la puerta.'
              : 'Here you register partners who send people to your business. Sign in to start — like putting a sign on the door.'}
          </p>
          {onSignIn ? (
            <button type="button" className="dashboard-security-inline-btn" onClick={onSignIn}>
              {spanish ? 'Iniciar sesión' : 'Sign in'}
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section className="inlet-console" aria-label="Inlet">
      <header className="inlet-chrome">
        <div>
          <h2>
            <span aria-hidden="true">🌊 </span>
            Inlet
            <HelpBadge label={spanish ? 'Qué es Inlet' : 'What Inlet is'}>
              <p>
                {spanish
                  ? 'Inlet lleva la cuenta de socios y visitas que envían. Pursuit es el libro de ventas. Un boletín no es una venta hasta que piden prueba o demo.'
                  : 'Inlet keeps the partner guest book. Pursuit is the sales book. A newsletter signup is not a sale until they ask for a trial or demo.'}
              </p>
            </HelpBadge>
          </h2>
          <p className="inlet-lede">
            {spanish
              ? 'Registre un socio, copie su enlace especial, y vea quién llegó. Tres pasos. Colores amables. Sin magia negra.'
              : 'Register a partner, copy their special link, and see who arrived. Three steps. Friendly colors. No dark magic.'}
          </p>
        </div>
        <button type="button" className="dashboard-security-inline-btn" onClick={() => void load(selectedCode)} disabled={busy}>
          {busy ? (spanish ? 'Cargando…' : 'Loading…') : (spanish ? 'Actualizar' : 'Refresh')}
        </button>
      </header>
      {error ? <p className="inlet-error" role="alert">{error}</p> : null}
      {partners.length === 0 ? (
        <div className="inlet-hero-empty" role="status">
          <div className="inlet-hero-empty-emoji" aria-hidden="true">🤝</div>
          <strong>{spanish ? 'Aún no tiene socios' : 'No partners yet'}</strong>
          <p>
            {spanish
              ? 'Un socio es un amigo o empresa que manda clientes. Escriba un código corto (como “maria-cafe”), un nombre, y cuánto gana (opcional). Luego pulse Registrar.'
              : 'A partner is a friend or company that sends customers. Type a short code (like “maria-cafe”), a name, and how much they earn (optional). Then tap Register.'}
          </p>
        </div>
      ) : null}
      <div className="inlet-grid">
        <article className="inlet-card inlet-card-accent">
          <h3>{spanish ? '① Socios' : '① Partners'}</h3>
          {partners.length === 0 ? (
            <p className="inlet-empty">
              {spanish
                ? 'Use el formulario de abajo — es el primer paso.'
                : 'Use the form below — that is step one.'}
            </p>
          ) : (
            <div className="inlet-table-wrap">
              <table className="inlet-table">
                <thead>
                  <tr>
                    <th>{spanish ? 'Código' : 'Code'}</th>
                    <th>{spanish ? 'Nombre' : 'Name'}</th>
                    <th>
                      <GlossaryTerm
                        term="BPS"
                        definition={
                          spanish
                            ? 'Puntos base: 100 BPS = 1% de comisión. Ejemplo: 250 BPS = 2,5%.'
                            : 'Basis points: 100 BPS = 1% commission. Example: 250 BPS = 2.5%.'
                        }
                      >
                        BPS
                      </GlossaryTerm>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner) => (
                    <tr key={partner.code}>
                      <td>
                        <button type="button" onClick={() => void load(partner.code)}>
                          {partner.code}
                        </button>
                      </td>
                      <td>{partner.name}</td>
                      <td>{partner.commission_bps ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <form className="inlet-form" onSubmit={registerPartner}>
            <input
              aria-label={spanish ? 'Código de socio' : 'Partner code'}
              placeholder={spanish ? 'código-socio' : 'partner-code'}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
            />
            <input
              aria-label={spanish ? 'Nombre de socio' : 'Partner name'}
              placeholder={spanish ? 'Nombre' : 'Name'}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
            <label className="inlet-field-hint">
              <span>
                {spanish ? 'Comisión (' : 'Commission ('}
                <GlossaryTerm
                  term="BPS"
                  definition={
                    spanish
                      ? 'Puntos base: 100 = 1%. Déjelo en 0 si el socio no cobra.'
                      : 'Basis points: 100 = 1%. Leave 0 if the partner is unpaid.'
                  }
                >
                  BPS
                </GlossaryTerm>
                )
              </span>
              <input
                aria-label={spanish ? 'Comisión en puntos base' : 'Commission basis points'}
                type="number"
                min={0}
                max={10000}
                value={commissionBps}
                onChange={(event) => setCommissionBps(event.target.value)}
              />
            </label>
            <button type="submit" className="dashboard-security-inline-btn" disabled={busy}>
              {spanish ? '➕ Registrar socio' : '➕ Register partner'}
            </button>
          </form>
        </article>
        <article className="inlet-card inlet-card-sky">
          <h3>{spanish ? '② Portal del socio' : '② Partner portal'}</h3>
          {summary ? (
            <>
              <p>
                <strong>{summary.partner.name}</strong>
                {' · '}
                {summary.partner.code}
              </p>
              <p className="inlet-link-box">
                <span className="inlet-link-label">{spanish ? 'Enlace para compartir' : 'Share this link'}</span>
                <a href={summary.landing_url}>{summary.landing_url}</a>
              </p>
              <div className="inlet-stats">
                <div className="inlet-stat inlet-stat-events">
                  <strong>{summary.conversion_count}</strong>
                  <span>{spanish ? 'Visitas / eventos' : 'Visits / events'}</span>
                </div>
                <div className="inlet-stat inlet-stat-assistant">
                  <strong>{summary.assistant_capture_count}</strong>
                  <span>{spanish ? 'Chat' : 'Chat'}</span>
                </div>
                <div className="inlet-stat inlet-stat-news">
                  <strong>{summary.newsletter_count}</strong>
                  <span>{spanish ? 'Boletín' : 'Newsletter'}</span>
                </div>
                <div className="inlet-stat inlet-stat-paid">
                  <strong>{summary.trial_paid_count}</strong>
                  <span>{spanish ? 'Pagó' : 'Paid'}</span>
                </div>
              </div>
            </>
          ) : (
            <p className="inlet-empty">
              {spanish
                ? 'Elija un socio a la izquierda (o registre uno) para ver su enlace mágico.'
                : 'Pick a partner on the left (or register one) to see their magic link.'}
            </p>
          )}
          <h3>{spanish ? '③ Quién llegó' : '③ Who showed up'}</h3>
          {(summary?.recent || conversions).length === 0 ? (
            <p className="inlet-empty">
              {spanish
                ? 'Todavía vacío — cuando alguien use el enlace del socio, aparece aquí.'
                : 'Still empty — when someone uses the partner link, they show up here.'}
            </p>
          ) : (
            <div className="inlet-table-wrap">
              <table className="inlet-table">
                <thead>
                  <tr>
                    <th>{spanish ? 'Tipo' : 'Type'}</th>
                    <th>Email</th>
                    <th>{spanish ? 'Socio' : 'Partner'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.recent || conversions).slice(0, 25).map((row, index) => (
                    <tr key={row.event_id || `${row.event_type}-${index}`}>
                      <td>{row.event_type}</td>
                      <td>{row.email || '—'}</td>
                      <td>{row.partner_code || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      </div>
    </section>
  );
};

export default InletWorkspace;
